import { ProcessGeneral } from 'api/endpoints/processes';
import { TmsConnectionDetails } from 'api/endpoints/tmsConnections';
import BaseRepo from 'container/BaseRepo';
import { buildBadRequestApiError } from 'container/helpers/errors';
import { ApiResponseAsync, isApiError } from 'container/models';
import { IGeneralPayablesScenarioRepo } from 'features/generalPayables/services/scenarios/IGeneralPayablesScenarioRepo';
import {
  CreateGeneralPayablesScenarioRequest,
  GeneralPayablesScenario,
  UpdateScenarioRequestType,
} from 'features/generalPayables/types';
import { TmsDataModel } from 'features/shared/licensedModules/types';

import {
  GeneralPayablesSearchResult,
  SearchGeneralPayablesScenariosRequest,
  SearchGLAccountsRequest,
  TruckMateFinanceGlAccount,
} from '../typing';

export class GeneralPayablesScenariosRepo
  extends BaseRepo
  implements IGeneralPayablesScenarioRepo
{
  search = async (
    draft: ProcessGeneral,
    request: SearchGeneralPayablesScenariosRequest
  ): ApiResponseAsync<GeneralPayablesScenario[]> => {
    const link = draft._links?.['searchGeneralPayablesScenarios'];

    if (!link) {
      return buildBadRequestApiError(
        new Error('link "search General Payables scenarios" is not available')
      );
    }

    const searchResult =
      await this.helpers.executeSearch<GeneralPayablesSearchResult>(
        link,
        request,
        { convertRequestToSnakeCase: true }
      );
    if (isApiError(searchResult)) {
      return searchResult;
    }
    return searchResult ?? [];
  };

  create = async (
    draft: ProcessGeneral,
    request: CreateGeneralPayablesScenarioRequest
  ): ApiResponseAsync<GeneralPayablesScenario> => {
    const link = draft._links?.['createGeneralPayablesScenario'];

    if (!link)
      return buildBadRequestApiError(
        new Error('link "create General Payables scenario" is not available')
      );

    return this.helpers.handlePost(link.href, request);
  };

  update = async (
    scenario: GeneralPayablesScenario,
    request: UpdateScenarioRequestType
  ): ApiResponseAsync<GeneralPayablesScenario> => {
    const link = scenario._links?.['update'];
    if (!link)
      return buildBadRequestApiError(
        new Error('link "update" is not available')
      );

    return this.helpers.executePatch(link, request);
  };

  setActive = async (
    scenario: GeneralPayablesScenario,
    activate: boolean
  ): ApiResponseAsync<GeneralPayablesScenario> => {
    if ((!scenario.isActive && !activate) || (scenario.isActive && activate)) {
      return scenario;
    }

    const linkName = activate ? 'activate' : 'deactivate';
    const link = scenario._links?.[linkName];
    if (!link)
      return buildBadRequestApiError(
        new Error(`link "${linkName}" is not available`)
      );

    return this.helpers.handlePost(link.href, undefined);
  };

  delete = async (
    scenario: GeneralPayablesScenario
  ): ApiResponseAsync<void> => {
    const link = scenario._links?.['delete'];
    if (!link)
      return buildBadRequestApiError(
        new Error('link "delete scenario" is not available')
      );

    return this.helpers.handleDelete(link.href);
  };

  tmsHasGeneralPayablesModel = (tms: TmsConnectionDetails): boolean =>
    tms._links?.['getGeneralPayablesModel'] !== undefined;

  getTMSGeneralPayablesModel = async (
    tms: TmsConnectionDetails
  ): ApiResponseAsync<TmsDataModel> => {
    const link = tms._links?.['getGeneralPayablesModel'];

    if (!link)
      return buildBadRequestApiError(
        new Error('link "get General Payables model" is not available')
      );

    const url = new URL(link.href);
    url.searchParams.append('tms_version', '1');
    return this.helpers.handleGet(url.toString());
  };

  searchGLAccounts = async (
    scenario: GeneralPayablesScenario,
    request: SearchGLAccountsRequest
  ): ApiResponseAsync<Array<TruckMateFinanceGlAccount>> => {
    const link = scenario._links?.['searchTruckmateFinanceGlAccounts'];
    if (!link)
      return buildBadRequestApiError(
        new Error(
          'link "search Truckmate Finance GL Accounts" is not available'
        )
      );

    return this.helpers.executeSearch(link, request, {
      convertRequestToSnakeCase: false,
    });
  };
}
